import {
    AsOfBadge,
    Badge,
    DateTime,
    type ListColumnDef,
    MerchantName,
    PageHeader,
    PublicId,
    StatusBadge,
} from '@bemor/web-kit/ui';

import type { StaffStoreRow } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';

const PATH = '/staff/stores';
const PAGE_KEY = 'stores';

/** BM-93 (design 0.9.0 `backoffice/stores`): every store of the tenant, the ones waiting for approval first. */
export const storeColumns: ListColumnDef<StaffStoreRow>[] = [
    {
        key: 'display_name',
        aliases: ['name'],
        label: 'Store',
        sortKey: 'display_name',
        cell: (s) => (
            <span className="flex min-w-0 flex-col">
                <span className="font-medium">{s.display_name}</span>
                {s.pending_display_name ? (
                    <span className="text-warning-fg text-xs">
                        Name change waiting: {s.pending_display_name}
                    </span>
                ) : s.name !== s.display_name ? (
                    <span className="text-muted-foreground text-xs">
                        {s.name}
                    </span>
                ) : null}
            </span>
        ),
        csv: (s) => s.display_name,
        locked: 'start',
    },
    {
        key: 'id',
        label: 'Id',
        cell: (s) => <PublicId id={s.id} />,
        csv: (s) => s.id,
    },
    {
        key: 'merchant',
        label: 'Merchant',
        sortKey: 'merchant_id',
        cell: (s) => (
            <MerchantName
                merchant={{ display_name: s.merchant.display_name }}
            />
        ),
        csv: (s) => s.merchant.display_name,
    },
    {
        key: 'organization',
        label: 'Organization',
        cell: (s) => s.organization?.name ?? '—',
        csv: (s) => s.organization?.name ?? null,
    },
    {
        key: 'status',
        label: 'Status',
        sortKey: 'status',
        cell: (s) => <StatusBadge status={s.status} domain="store" />,
        csv: (s) => s.status,
        groupable: { key: 'status', statusDomain: 'store' },
    },
    {
        key: 'is_default',
        label: 'Default',
        cell: (s) =>
            s.is_default ? (
                <Badge variant="neutral" emphasis="outline" size="sm">
                    Default
                </Badge>
            ) : null,
        csv: (s) => (s.is_default ? 'yes' : 'no'),
        noTruncate: true,
    },
    {
        key: 'created_at',
        aliases: ['created'],
        label: 'Since',
        kind: 'datetime',
        sortKey: 'created_at',
        cell: (s) => <DateTime value={s.created_at} format="date" empty="—" />,
        csv: (s) => s.created_at,
        noTruncate: true,
    },
];

export const storeFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    merchant_id: 'Merchant',
    organization_id: 'Organization',
    range: 'Created',
});

export const storesPrefetch = adminListPrefetch(PAGE_KEY, PATH);

export function StoresPage() {
    const { state, query } = useAdminList<StaffStoreRow>(PAGE_KEY, PATH, {
        columns: storeColumns,
        filters: storeFilters,
    });
    return (
        <div>
            <PageHeader
                title="Stores"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Every store of the tenant, the ones waiting for approval first. Approve, reject or suspend from the detail page.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="stores"
                state={state}
                query={query}
                rowKey={(s) => s.id}
                rowHref={(s) => `/stores/${s.id}`}
                controls={{
                    q: { kind: 'search' },
                    status: { domain: 'store' },
                    merchant_id: { kind: 'text', label: 'Merchant id' },
                    organization_id: { kind: 'text', label: 'Organization id' },
                }}
            />
        </div>
    );
}
